/**
 * A modal window is a graphical control element subordinate to an application&#x27;s main window. It creates a mode that disables the main window but keeps it visible, with the dialog window as a child window in front of it
 *
 * @module views/Layouts/OverlayModal
 */
import React, { forwardRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { isServer } from '../../../utils/device_detection/device_detection';
import FocusLock, { InFocusGuard } from 'react-focus-lock'
import './OverlayModal.scss';

import Icon from '../../components/Icon/Icon';
import PropTypes from 'prop-types';

const OverlayModal = forwardRef( ( props, ref ) => {
  const {
    children,
    alignment,
    isVideoModal,
    videoMeta,
    closeAccessibilityAction,
    customClassName,
    isSwatchModal
  } = props;

  if( isServer() ){
    return null
  }

  const style = videoMeta ? {
    width: `${videoMeta.width}px`,
    height: `${videoMeta.height}px`
  } : {}

  const component = (
    <FocusLock group='videoGroup'>
      <div
        className={ `${OVERLAY_MODAL_CLASS}__wrapper` }
        role='dialog'
        aria-modal='true'
      >
        <dialog
          ref={ ref }
          className={ classNames( OVERLAY_MODAL_CLASS, {
            ...( alignment && { [`${OVERLAY_MODAL_CLASS}--${alignment}`]: alignment } ),
            ...( isVideoModal && { [`${OVERLAY_MODAL_CLASS}--modalVideo`]: isVideoModal } ),
            ...( isSwatchModal && { [`${OVERLAY_MODAL_CLASS}--modalSwatch`]: isSwatchModal } ),
            ...( customClassName && { [`${customClassName}`]: customClassName } )
          } ) }
          style={ style }
        >
          <form method='dialog'>
            <button
              className={ `${OVERLAY_MODAL_CLASS}__close` }
              aria-label={ closeAccessibilityAction.label }
              type='submit'
            >
              <Icon size='s'
                name={ 'X' }
              />
            </button>
          </form>
          <InFocusGuard />
          { children }
          <InFocusGuard />
        </dialog>
      </div>
    </FocusLock>
  )

  return createPortal( component, document.body )
} );

/**
 * Overlay Modal class
 * @constant {string}
 */
export const OVERLAY_MODAL_CLASS = 'OverlayModal'

/**
 * Default values for passed properties
 * @type {object}
 * @property {object} closeAccessibilityAction - Sets close accessibility label and graphql
 */

// TODO: remove default label value when content model is update.

export const defaultProps = {
  alignment: 'center',
  closeAccessibilityAction: {
    label: 'close',
    graphql: ''
  }
};

/**
  * Property type definitions
  * @type {object}
  * @property {object} closeAccessibilityAction - Sets close accessibility label and graphql
  */
export const propTypes = {
  /** Sets close accessibility label and graphql */
  closeAccessibilityAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } )
}

OverlayModal.propTypes = propTypes;
OverlayModal.defaultProps = defaultProps;

export default OverlayModal;
